import { useEffect, useState } from "react";
import { X, CheckCircle2, Calendar, Clock, MapPin, User, Mail, Phone, Store, AlertCircle } from "lucide-react";
import { useStores, useCurrentMember } from "@/lib/data-hooks";
import { supabase } from "@/integrations/supabase/client";
import { notifyParentModalOpen } from "@/lib/embed-modal";

type Props = {
  open: boolean;
  onClose: () => void;
  benefitDefinitionId: string | null;
  benefitTitle: string;
  onConfirmed: () => void;
};

const TIME_SLOTS = ["10:00", "10:30", "11:00", "11:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"];

function nextWeekdays(count: number) {
  const out: { value: string; label: string }[] = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const fmt = new Intl.DateTimeFormat("sv-SE", { weekday: "short", day: "numeric", month: "short" });
  while (out.length < count) {
    const day = d.getDay();
    if (day !== 0) {
      out.push({ value: d.toISOString().slice(0, 10), label: fmt.format(d) });
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export function BookingModal({ open, onClose, benefitDefinitionId, benefitTitle, onConfirmed }: Props) {
  const member = useCurrentMember();
  const { data: stores = [] } = useStores();

  const [step, setStep] = useState<"form" | "done">("form");
  const [name, setName] = useState(member ? `${member.first_name} ${member.last_name}` : "");
  const [email, setEmail] = useState(member?.email ?? "");
  const [phone, setPhone] = useState(member?.phone ?? "");
  const [storeId, setStoreId] = useState<string>("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ code: string; storeName: string } | null>(null);

  const days = nextWeekdays(14);
  const selectedStore = stores.find((s: any) => s.id === storeId);
  const valid = name && email && phone && storeId && date && time;

  useEffect(() => { if (open) notifyParentModalOpen(); }, [open]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || !benefitDefinitionId) return;
    setSubmitting(true);
    setError(null);
    try {
      const bookingAt = new Date(`${date}T${time}:00`).toISOString();
      const { data, error: rpcError } = await supabase.rpc("book_benefit" as any, {
        _benefit_definition_id: benefitDefinitionId,
        _store_id: storeId,
        _booking_at: bookingAt,
        _message: message || null,
      });
      if (rpcError) throw rpcError;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.activation_code) throw new Error("Kunde inte skapa bokning");
      setResult({ code: row.activation_code, storeName: row.store_name || selectedStore?.name || "" });
      setStep("done");
      onConfirmed();
    } catch (err: any) {
      setError(err?.message || "Något gick fel vid bokning");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep("form");
    setDate(""); setTime(""); setMessage(""); setError(null); setResult(null);
  };
  const close = () => { reset(); onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-embed-modal>
      <div className="bg-white rounded-2xl max-w-2xl w-full my-8 relative shadow-2xl" onClick={e => e.stopPropagation()}>
        <button className="absolute top-4 right-4 text-muted-foreground hover:text-foreground z-10" onClick={close} aria-label="Stäng"><X size={20} /></button>

        {step === "form" && (
          <div className="p-6 md:p-8">
            <div className="mb-6">
              <div className="text-xs font-display font-bold tracking-widest uppercase text-primary mb-1">Boka tid</div>
              <h3 className="!text-2xl font-display font-bold">{benefitTitle}</h3>
              <p className="text-sm text-muted-foreground mt-1">Välj butik, datum och tid. När du bekräftar genereras en kod som personalen löser in i butik.</p>
            </div>

            {error && (
              <div className="mb-5 flex items-start gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20 text-sm">
                <AlertCircle size={16} className="text-destructive shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={submit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label-field"><User size={12} className="inline mr-1" /> Namn</label>
                  <input value={name} onChange={e => setName(e.target.value)} className="input-field" required />
                </div>
                <div>
                  <label className="label-field"><Phone size={12} className="inline mr-1" /> Telefon</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} className="input-field" required />
                </div>
              </div>

              <div>
                <label className="label-field"><Mail size={12} className="inline mr-1" /> E-post</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input-field" required />
              </div>

              <div>
                <label className="label-field"><Store size={12} className="inline mr-1" /> Välj butik</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {stores.map((s: any) => (
                    <button key={s.id} type="button" onClick={() => setStoreId(s.id)}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-display font-semibold transition ${storeId === s.id ? "border-primary bg-primary/5 text-primary" : "border-[var(--color-border)] hover:border-primary/40"}`}>
                      {s.name}
                    </button>
                  ))}
                  {!stores.length && <div className="col-span-full text-xs text-muted-foreground">Inga butiker upplagda än.</div>}
                </div>
              </div>

              <div>
                <label className="label-field"><Calendar size={12} className="inline mr-1" /> Välj datum</label>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                  {days.map(d => (
                    <button key={d.value} type="button" onClick={() => setDate(d.value)}
                      className={`shrink-0 px-3 py-2 rounded-lg border text-xs font-display font-semibold transition capitalize ${date === d.value ? "border-primary bg-primary text-white" : "border-[var(--color-border)] hover:border-primary/40"}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-field"><Clock size={12} className="inline mr-1" /> Välj tid</label>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {TIME_SLOTS.map(t => (
                    <button key={t} type="button" onClick={() => setTime(t)}
                      className={`px-2 py-2 rounded-lg border text-sm font-display font-semibold transition ${time === t ? "border-primary bg-primary text-white" : "border-[var(--color-border)] hover:border-primary/40"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-field">Meddelande (valfritt)</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} className="input-field" placeholder="Tex modell, problem eller önskemål..." />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end pt-2 border-t border-[var(--color-border)]">
                <button type="button" onClick={close} className="btn-pill-outline">Avbryt</button>
                <button type="submit" disabled={!valid || submitting} className="btn-pill disabled:opacity-50 disabled:cursor-not-allowed">
                  {submitting ? "Bokar..." : "Bekräfta bokning"}
                </button>
              </div>
            </form>
          </div>
        )}

        {step === "done" && result && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[var(--color-success)]/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="text-[var(--color-success)]" size={36} />
            </div>
            <h3 className="!text-2xl font-display font-bold mb-2">Bokningen är bekräftad!</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Visa koden nedan för personalen i butiken vid ditt besök.
            </p>

            <div className="font-mono font-bold tabular-nums tracking-[0.25em] text-4xl sm:text-5xl py-5 rounded-2xl bg-primary/10 text-primary mb-6 select-all">
              {result.code}
            </div>

            <div className="card-soft p-5 text-left max-w-sm mx-auto mb-6 bg-muted">
              <div className="text-xs font-display font-bold tracking-wider uppercase text-muted-foreground mb-3">Din bokning</div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><MapPin size={14} className="text-primary" /> {result.storeName}</div>
                <div className="flex items-center gap-2"><Calendar size={14} className="text-primary" /> {new Date(date).toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" })}</div>
                <div className="flex items-center gap-2"><Clock size={14} className="text-primary" /> kl. {time}</div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mb-4">Du kan boka samma förmån igen efter angiven karenstid.</p>
            <button onClick={close} className="btn-pill">Klart</button>
          </div>
        )}
      </div>
    </div>
  );
}
